/**
 * Core-100 CORE-P (owner direction 4) — Team compatibility, for the shop's managers and owners.
 *
 * Shows, for technicians who share jobs, how shared work goes compared with each person's solo work —
 * with the sample size, a 95% range and the evidence. The combination is the subject; the list is
 * ordered by evidence, not by effect, and is never a ranking of people. Nothing here changes
 * assignments. The server (team_compatibility_evidence) refuses anyone but the shop's managers/owners.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  assessTeamCompatibility, describeTeamPeriod, MIN_SHARED_JOBS, TEAM_COMPATIBILITY_PERIOD_RPC, teamCompatibilityPeriodArgs,
  type TeamEvidenceRow,
} from '@/lib/teamCompatibility/teamCompatibility';

const PERIODS = [
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
  { value: '365', label: 'Last 12 months' },
] as const;

const STATUS_LABEL: Record<string, string> = {
  insufficient_evidence: 'Not enough shared jobs yet',
  no_baseline: 'No solo baseline',
  no_clear_difference: 'No clear difference',
  more_time_together: 'Shared work takes longer',
  less_time_together: 'Shared work goes faster',
};

export function TeamCompatibilityPanel() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;
  const [days, setDays] = useState<string>('90');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['team-compatibility', shopId, days],
    enabled: !!shopId,
    queryFn: async () => {
      // The period is N shop-local calendar days, resolved by the server (CORE-P time boundary).
      const { data: rows, error: rpcError } = await supabase.rpc(
        TEAM_COMPATIBILITY_PERIOD_RPC as never, teamCompatibilityPeriodArgs(shopId as string, days) as never,
      );
      if (rpcError) throw rpcError;
      const evidence = (rows ?? []) as TeamEvidenceRow[];
      const ids = [...new Set(evidence.flatMap((r) => [r.technician_a, r.technician_b]).filter(Boolean))] as string[];
      let names: Record<string, string> = {};
      if (ids.length > 0) {
        const { data: techs, error: techError } = await supabase.from('technicians').select('id, name').in('id', ids);
        if (techError) throw techError;
        names = Object.fromEntries((techs ?? []).map((t) => [t.id, t.name]));
      }
      return { assessments: assessTeamCompatibility(evidence, names), period: describeTeamPeriod(evidence) };
    },
  });

  const assessments = data?.assessments ?? [];
  const shown = assessments.filter((a) => a.status !== 'insufficient_evidence');
  const belowThreshold = assessments.length - shown.length;

  return (
    <Card data-testid="team-compatibility-panel">
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle>Team compatibility</CardTitle>
            <CardDescription>
              How shared jobs go when two technicians work them together, compared with each one's solo work.
              Shown only after {MIN_SHARED_JOBS} shared jobs. It describes the working combination — never a person — and does not change assignments.
            </CardDescription>
          </div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {data?.period && <p className="text-xs text-muted-foreground" data-testid="team-compatibility-period">Period: {data.period}</p>}
        {isError && (
          <p role="alert" className="text-sm text-destructive">
            {String((error as { message?: string })?.message ?? '').includes('TEAM_COMPATIBILITY_FORBIDDEN')
              ? 'Team compatibility is visible to the shop\'s managers and owners only.'
              : 'Could not load team compatibility.'}
          </p>
        )}
        {data && shown.length === 0 && (
          <p className="text-sm text-muted-foreground">No pair has enough shared jobs in this period to report on.</p>
        )}
        {shown.map((a) => (
          <div key={`${a.technicianA}:${a.technicianB}`} className="rounded-md border p-3 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{STATUS_LABEL[a.status]}</Badge>
              <span className="text-xs text-muted-foreground">{a.sharedJobs} shared jobs · confidence {a.confidence}</span>
            </div>
            <p className="text-sm">{a.explanation}</p>
          </div>
        ))}
        {belowThreshold > 0 && (
          <p className="text-xs text-muted-foreground">
            {belowThreshold} other pair{belowThreshold === 1 ? '' : 's'} shared fewer than {MIN_SHARED_JOBS} jobs and {belowThreshold === 1 ? 'is' : 'are'} not shown.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
